import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Autocomplete,
  TextField,
  Card,
  CardContent,
  CardActions,
  Grid,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import TenantSelector from '../../components/TenantSelector/TenantSelector';
import { MDButton } from '../../components/MDButton';
import useSnackbar from '../../hooks/useSnackbar';
import { Severity } from '../../types';
import { ConfirmationDialog } from '../Claims/components/ConfirmationDialog';
import {
  getWhatsAppMembers,
  sendWhatsAppMessage,
  WhatsAppMember,
} from '../../services/whatsappDemoService';

type MessageConfig = {
  id: string;
  title: string;
  description: string;
  messageType: string;
};

const MESSAGE_CONFIGS: MessageConfig[] = [
  {
    id: 'findFacility',
    title: 'Find Facility',
    description: 'Send a WhatsApp message with a button to help the member find a nearby facility.',
    messageType: 'findFacility',
  },
  {
    id: 'reminder',
    title: 'Appointment Reminder',
    description: 'Send a reminder notification about an upcoming appointment.',
    messageType: 'reminder',
  },
  {
    id: 'openingMessage',
    title: 'Opening Message',
    description: 'Send a WhatsApp opening message with an image to welcome the member.',
    messageType: 'openingMessage',
  },
  {
    id: 'cardMessage',
    title: 'Card Message',
    description: 'Send a WhatsApp message with the member\'s insurance card image.',
    messageType: 'cardMessage',
  },
];

export const WhatsAppDemoPage: React.FC = () => {
  const [carrierId, setCarrierId] = useState<number>(0);
  const [members, setMembers] = useState<WhatsAppMember[]>([]);
  const [memberSearchInput, setMemberSearchInput] = useState<string>('');
  const [memberSearchLoading, setMemberSearchLoading] = useState<boolean>(false);
  const [selectedMember, setSelectedMember] = useState<WhatsAppMember | null>(null);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    configId: string | null;
  }>({ open: false, configId: null });

  const show = useSnackbar();
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setSelectedMember(null);
    setMembers([]);
    setMemberSearchInput('');
  }, [carrierId]);

  const handleMemberSearch = (inputValue: string) => {
    setMemberSearchInput(inputValue);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (inputValue.length < 2) {
      setMembers([]);
      return;
    }
    searchTimeoutRef.current = setTimeout(async () => {
      setMemberSearchLoading(true);
      try {
        const results = await getWhatsAppMembers(carrierId, inputValue);
        setMembers(results);
      } catch (e) {
        show('Failed to search members', Severity.error);
      } finally {
        setMemberSearchLoading(false);
      }
    }, 400);
  };

  const handleSendMessage = async (config: MessageConfig) => {
    if (!selectedMember) return;

    setProcessingId(config.id);
    try {
      const result = await sendWhatsAppMessage(selectedMember.id, config.messageType);
      if (result.success) {
        show('Message sent successfully!', Severity.success);
      } else {
        show('Failed to send message', Severity.error);
      }
    } catch (error) {
      show('Failed to send message', Severity.error);
    } finally {
      setProcessingId(null);
    }
  };

  const openConfirmDialog = (configId: string) => {
    setConfirmDialog({ open: true, configId });
  };

  const handleConfirmSend = async () => {
    const config = MESSAGE_CONFIGS.find((c) => c.id === confirmDialog.configId);
    if (!config) return;

    setConfirmDialog({ open: false, configId: null });
    await handleSendMessage(config);
  };

  const handleCancelSend = () => {
    setConfirmDialog({ open: false, configId: null });
  };

  const selectedConfig = MESSAGE_CONFIGS.find((c) => c.id === confirmDialog.configId);
  const confirmMessage = selectedConfig && selectedMember
    ? `Are you sure you want to send "${selectedConfig.title}" to ${selectedMember.firstName} ${selectedMember.lastName}?`
    : 'Are you sure you want to send this message?';

  return (
    <Box sx={{ maxWidth: 1200, margin: '0 auto', p: 3 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
          WhatsApp Demo
        </Typography>

        {/* Section 1: Tenant/Carrier Selector */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
            Step 1: Select Carrier
          </Typography>
          <TenantSelector
            value={carrierId}
            onChange={(val) => setCarrierId(val)}
            includeAllOption={false}
          />
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Section 2: Member Selector */}
        {carrierId > 0 && (
          <>
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                Step 2: Select Member
              </Typography>
              <Autocomplete
                options={members}
                getOptionLabel={(member) =>
                  `${member.firstName} ${member.lastName}`
                }
                value={selectedMember}
                onChange={(event, newValue) => setSelectedMember(newValue)}
                inputValue={memberSearchInput}
                onInputChange={(event, newInputValue) => handleMemberSearch(newInputValue)}
                loading={memberSearchLoading}
                freeSolo={false}
                filterOptions={(x) => x}
                noOptionsText={memberSearchInput.length < 2 ? 'Type a name to search...' : 'No members found'}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select a member"
                    variant="outlined"
                    placeholder="Search by member name..."
                  />
                )}
                sx={{ maxWidth: 500 }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </Box>

            <Divider sx={{ my: 4 }} />
          </>
        )}

        {/* Section 3: Message Cards */}
        {selectedMember && (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mb: 2 }}>
              Step 3: Send Message
            </Typography>
            <Alert severity="info" sx={{ mb: 3 }}>
              Sending to: <strong>{selectedMember.firstName} {selectedMember.lastName}</strong> ({selectedMember.phone})
            </Alert>
            <Grid container spacing={3}>
              {MESSAGE_CONFIGS.map((config) => (
                <Grid item xs={12} sm={6} md={4} key={config.id}>
                  <Card
                    variant="outlined"
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'box-shadow 0.3s',
                      '&:hover': {
                        boxShadow: 3,
                      },
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                        {config.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {config.description}
                      </Typography>
                    </CardContent>
                    <CardActions sx={{ p: 2, pt: 0 }}>
                      <MDButton
                        variant="contained"
                        color="error"
                        fullWidth
                        onClick={() => openConfirmDialog(config.id)}
                        disabled={processingId === config.id}
                      >
                        {processingId === config.id ? 'Sending...' : 'Send Message'}
                      </MDButton>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Empty state when no carrier selected */}
        {carrierId === 0 && (
          <Alert severity="info">
            Please select a carrier to get started.
          </Alert>
        )}

        {/* Empty state when carrier selected but no member */}
        {carrierId > 0 && !selectedMember && (
          <Alert severity="info">
            Please select a member to send WhatsApp messages.
          </Alert>
        )}
      </Paper>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmDialog.open}
        title="Confirm Send Message"
        message={confirmMessage}
        onConfirm={handleConfirmSend}
        onCancel={handleCancelSend}
        confirmText="Send"
        cancelText="Cancel"
        loading={!!processingId}
      />
    </Box>
  );
};

export default WhatsAppDemoPage;
